import React from 'react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png"
                alt="doorush Logo"
                className="h-10 w-auto"
              />
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto prose prose-lg text-gray-700 leading-relaxed">

          <h1 className="text-4xl font-bold text-gray-900">
            Terms and Conditions
          </h1>
          <p className="text-gray-600">
            Last Updated on 02/11/2025
          </p>

          <p>
            These Terms and Conditions ("Terms") govern your access to and use of
            the services made available on or through the doorush mobile
            application and website (together referred to as the "Platform").
            The Platform is owned and operated by Holbewoner Technologies Private
            Limited ("doorush", "we", "us", or "our").
          </p>

          <p>
            These Terms apply to all users of the Platform, including individuals
            or entities seeking household services such as cooking, cleaning,
            and other domestic tasks (referred to as "users," "customers," or
            "you"). By registering for or using any part of the Platform, you
            agree to be legally bound by these Terms. If you do not accept these
            Terms, please do not use the Platform.
          </p>

          <p>
            Please read these Terms carefully before using the Platform. Your
            use of the Platform constitutes your understanding and acceptance
            of these Terms, including any future amendments.
          </p>

          <h2>1. Introduction to Our Services</h2>

          <p>
            <strong>1.1</strong> doorush operates as a digital marketplace that
            facilitates the connection between users and third-party service
            providers. These providers include:
          </p>

          <p>
            <strong>"Quick Help"</strong> partners, who offer a wide range of
            household assistance including but not limited to cleaning, mopping,
            dishwashing, laundry, organizing, packing, and unpacking.
          </p>

          <p>
            <strong>"Insta Cook"</strong> professionals, who are specialized in
            offering on-the-spot cooking assistance using the customer’s
            ingredients at their premises.
          </p>

          <p>
            These providers ("Service Providers") operate independently and are
            not employees or agents of doorush. They work at their own
            discretion, and doorush is not responsible for their conduct or
            service outcomes.
          </p>

          <p>
            <strong>1.2</strong> doorush enables service discovery, appointment
            scheduling, payment processing, and customer support. However,
            doorush does not directly provide any Helping Services and shall not
            be held liable for any dissatisfaction, harm, or damages caused by
            the services rendered by a third-party provider.
          </p>

          <p>
            <strong>1.3</strong> While doorush verifies Service Providers through
            ID checks, basic background review, and occasional training
            sessions, it does not guarantee complete safety, background
            accuracy, or skill verification. Users are encouraged to exercise
            reasonable judgment.
          </p>

          <p>
            <strong>1.4</strong> The Platform is intended strictly for personal,
            residential, and non-commercial use within the territorial
            jurisdiction of India. Commercial or illegal use is strictly
            prohibited and may result in termination of access.
          </p>

          <p>
            <strong>1.5</strong> By registering and using the Platform, users
            consent to receive transactional and promotional communications
            from doorush via phone calls, SMS, emails, in-app messages, or
            WhatsApp, in accordance with the Information Technology Act, 2000
            and the applicable TRAI regulations. You may opt out of promotional
            communications through the Platform’s settings, though critical
            service-related communications will still be sent.
          </p>

          <h2>2. Account Registration and Responsibilities</h2>

          <p><strong>2.1</strong> To use the services offered by doorush, users must register using a valid Indian mobile number and furnish accurate information. A user must be 18 years of age or older to register.</p>

          <p><strong>2.2</strong> You are responsible for all activities carried out under your doorush account. You are advised to keep your login credentials secure and private.</p>

          <p><strong>2.3</strong> Use of the Platform for any fraudulent or unauthorized activity, including impersonation or providing false information, is strictly prohibited. doorush reserves the right to take appropriate legal action against such behavior.</p>

          <p><strong>2.4</strong> You agree to update your contact information promptly to ensure all notifications are properly delivered.</p>

          <p><strong>2.5</strong> You agree to not share your account with others or allow unauthorized access. If you suspect misuse, contact doorush support immediately.</p>

          <h2>3. Booking, Cancellation, and Scheduling</h2>

          <p><strong>3.1</strong> Users may place instant service requests or schedule future bookings via the Platform, subject to availability in their geographic region.</p>

          <p><strong>3.2</strong> All confirmed bookings will be communicated via SMS, email, or app notification. doorush reserves the right to reschedule or cancel bookings based on availability, weather conditions, safety concerns, or operational limitations.</p>

          <p><strong>3.3</strong> Users may cancel or reschedule bookings up to 30 minutes before the appointment time at no charge. Cancellations within 30 minutes of the scheduled service may incur a cancellation fee to compensate the Service Provider’s time and travel.</p>

          <p><strong>3.4</strong> If a Service Provider fails to show up, doorush will attempt to assign a replacement. If no replacement is available within a reasonable time, users will be eligible for a full refund.</p>

          <p><strong>3.5</strong> Services performed are limited to the tasks explicitly selected and paid for on the Platform. Additional tasks requested on the spot may require an add-on charge or fresh booking.</p>

          <p><strong>3.6</strong> Time extensions for ongoing services must be requested in-app or through customer support. These extensions are chargeable and subject to Service Provider availability.</p>

          <h2>4. Pricing, Payments, and Refund Policy</h2>

          <p><strong>4.1</strong> Pricing is dynamic and may vary based on location, time of day, demand, complexity of task, and service duration. Final charges will be displayed before booking confirmation.</p>

          <p><strong>4.2</strong> Accepted payment modes include UPI, debit/credit cards, wallets, and net banking. In some areas, cash on delivery may also be allowed.</p>

          <p><strong>4.3</strong> Any materials required during service must be provided by the user. If additional tools or materials are needed unexpectedly, the user may be billed separately.</p>

          <p><strong>4.4</strong> Refunds will only be provided under specific conditions:</p>
          <ul>
            <li>Service Provider was a no-show and a replacement could not be arranged;</li>
            <li>Service was not delivered at all due to Service Provider fault;</li>
            <li>Service delivered was grossly incomplete or defective and validated by doorush support upon investigation.</li>
          </ul>

          <p><strong>4.5</strong> Refunds must be requested by writing to support@doorush.in within 24 hours of service. Requests submitted after this window may not be entertained.</p>

          <p><strong>4.6</strong> Refund approvals are at the discretion of doorush and may require photographic or written proof. Processing times may vary but typically take up to 7 business days.</p>

          <p><strong>4.7</strong> doorush retains the right to modify service charges and apply promotional pricing or discounts during specific campaigns or festivals.</p>

          <h2>5. User Obligations and Safety</h2>

          <p><strong>5.1</strong> You must provide a clean, hazard-free environment for the Service Provider to work in.</p>
          <p><strong>5.2</strong> Ensure that pets are restrained and any hazardous material or objects are safely stored before service begins.</p>
          <p><strong>5.3</strong> You or an authorized adult representative must be present during the service, unless otherwise agreed in writing.</p>
          <p><strong>5.4</strong> Service Providers should not be left unattended in sensitive areas or around minors.</p>
          <p><strong>5.5</strong> Users must provide all necessary ingredients, appliances, or cleaning supplies unless otherwise specified during booking.</p>
          <p><strong>5.6</strong> doorush disclaims responsibility for loss or theft unless gross negligence by doorush is proven. Secure all valuables prior to service.</p>
          <p><strong>5.7</strong> Users must not coerce or bribe Service Providers into delivering tasks outside the Platform. This is grounds for immediate account suspension.</p>

          <h2>6. Conduct and Platform Integrity</h2>

          <p><strong>6.1</strong> Users must treat Service Providers with courtesy, dignity, and fairness.</p>
          <p><strong>6.2</strong> doorush maintains a zero-tolerance policy for verbal abuse, physical threats, or any form of harassment.</p>
          <p><strong>6.3</strong> Attempts to bypass the Platform by directly hiring Service Providers may result in account termination and legal action.</p>
          <p><strong>6.4</strong> doorush reserves the right to monitor, audit, and flag accounts demonstrating patterns of abuse, excessive cancellations, or fraudulent behavior.</p>
          <p><strong>6.5</strong> All complaints must be lodged within 24 hours of service completion via support@doorush.in. Delayed complaints may be dismissed.</p>

          <h2>7. Privacy and Data Protection</h2>

          <p><strong>7.1</strong> doorush follows a strict Privacy Policy, which governs the collection, storage, and usage of user data.</p>
          <p><strong>7.2</strong> We use your information to facilitate service fulfillment, improve user experience, resolve disputes, and provide customer support.</p>
          <p><strong>7.3</strong> We share information only with trusted third-party service partners for payment processing, background checks, analytics, or operations.</p>
          <p><strong>7.4</strong> You may request access, correction, or deletion of your personal data, except where retention is required by law.</p>
          <p><strong>7.5</strong> doorush does not sell or rent user information to external parties.</p>

          <h2>8. Disclaimer of Warranties and Limitation of Liability</h2>

          <p><strong>8.1</strong> All services are provided on an "as-is" basis. doorush makes no warranty regarding quality, accuracy, or availability.</p>
          <p><strong>8.2</strong> You acknowledge that services are performed by third-party individuals and that outcomes may vary based on factors beyond our control.</p>
          <p><strong>8.3</strong> doorush is not liable for any damages, injury, or losses incurred during service, except in cases where gross negligence by doorush is established.</p>
          <p><strong>8.4</strong> In no event shall doorush’s total liability exceed the amount paid by the user for the booking in question.</p>
          <p><strong>8.5</strong> We are not liable for delays or failures caused by events outside our control, including natural disasters, strikes, service interruptions, or legal mandates.</p>

          <h2>9. Suspension and Termination</h2>

          <p><strong>9.1</strong> doorush reserves the right to suspend or terminate user accounts at any time due to policy violations, fraud detection, or legal non-compliance.</p>
          <p><strong>9.2</strong> If a user account is terminated, all ongoing and future bookings may be cancelled without liability.</p>
          <p><strong>9.3</strong> Users may voluntarily request account deletion via the app or by contacting customer support.</p>

          <h2>10. Intellectual Property Rights</h2>

          <p><strong>10.1</strong> All intellectual property on the Platform including text, graphics, software, trademarks, and user interface is owned or licensed by doorush.</p>
          <p><strong>10.2</strong> You may not copy, modify, distribute, or use content from the Platform for commercial purposes without prior written consent.</p>

          <h2>11. Governing Law and Jurisdiction</h2>

          <p><strong>11.1</strong> These Terms and any dispute arising from them shall be governed by the laws of India.</p>
          <p><strong>11.2</strong> All legal proceedings shall fall under the exclusive jurisdiction of the courts located in Chennai, Tamil Nadu.</p>

          <h2>12. General Provisions</h2>

          <p><strong>12.1</strong> doorush may update these Terms at any time. Continued use of the Platform signifies acceptance of the revised Terms.</p>
          <p><strong>12.2</strong> If any clause of these Terms is held unenforceable, the remaining clauses will continue to be valid.</p>
          <p><strong>12.3</strong> These Terms supersede all prior agreements between the user and doorush regarding the use of the Platform.</p>
          <p><strong>12.4</strong> doorush’s failure to enforce a right shall not constitute a waiver of that right.</p>

          <h2>13. Contact Details</h2>

          <p>
            Company: Holbewoner Technologies Private Limited<br />
            Email: support@doorush.in<br />
            Phone: +91 9092963743<br />
            Address: 1st floor, 1/2A, 12-B, 1st Main Road, Sowmya Nagar,
            Perumbakkam, Chennai - 600100
          </p>

          <p className="font-semibold mt-6">
            By continuing to access and use the doorush Platform, you confirm
            that you have read, understood, and agreed to these Terms and
            Conditions in their entirety.
          </p>

        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
